import type { Accessor, JSX, ParentComponent } from "solid-js";
import {
	createContext,
	createMemo,
	createSignal,
	onCleanup,
	onMount,
	splitProps,
	useContext,
} from "solid-js";
import "./Appearance.css";
import {
	type AppearanceScheme,
	invertAppearance,
	readDocumentAppearance,
	type ResolvedAppearance,
} from "./theme/appearance";

const AppearanceContext = createContext<Accessor<ResolvedAppearance>>();

const useDocumentAppearance = (): Accessor<ResolvedAppearance> => {
	const [scheme, setScheme] = createSignal<ResolvedAppearance>(
		readDocumentAppearance(),
	);

	onMount(() => {
		const sync = () => setScheme(readDocumentAppearance());
		sync();
		const observer = new MutationObserver(sync);
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["data-appearance"],
		});
		const media = window.matchMedia("(prefers-color-scheme: dark)");
		media.addEventListener("change", sync);
		onCleanup(() => {
			observer.disconnect();
			media.removeEventListener("change", sync);
		});
	});

	return scheme;
};

type AppearanceProps = {
	/** `inverse` flips the nearest ancestor scheme (or the document). */
	scheme: AppearanceScheme;
} & Omit<JSX.HTMLAttributes<HTMLDivElement>, "color">;

/**
 * Scheme boundary for a subtree. Sets `color-scheme` so `light-dark()` tokens
 * and controls resolve as light or dark, independently of the page.
 *
 * [Design System →](http://localhost:5174/component/Appearance)
 */
export const Appearance: ParentComponent<AppearanceProps> = (props) => {
	const [local, rest] = splitProps(props, [
		"scheme",
		"children",
		"class",
		"classList",
		"ref",
	]);
	const parent = useContext(AppearanceContext);
	const documentAppearance = useDocumentAppearance();
	const resolved = createMemo((): ResolvedAppearance => {
		if (local.scheme === "light" || local.scheme === "dark") {
			return local.scheme;
		}
		return invertAppearance(parent?.() ?? documentAppearance());
	});

	return (
		<AppearanceContext.Provider value={resolved}>
			<div
				{...rest}
				ref={local.ref}
				class={`gvtui-appearance ${local.class ?? ""}`.trim()}
				classList={{
					[`gvtui-appearance--${resolved()}`]: true,
					...local.classList,
				}}
				data-appearance={resolved()}
			>
				{local.children}
			</div>
		</AppearanceContext.Provider>
	);
};
